import React from 'react';

import { QueryOptions } from '@apollo/client';

import type { Nullable } from '@clearverify/types';

import {
  FileResponseInfoFragment,
  GeneratedReportFilesArchiveResponse,
  PiiDataAccessActionType,
  useDownloadGeneratedReportFilesArchiveMutation,
  useDownloadGeneratedReportFilesMutation,
  useGeneratedReportsMetaQuery,
} from 'src/api';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { useNotification } from '../app';

type GeneratedReportsListHookParams = {
  inboxItemId?: Nullable<string>;
  skip?: boolean;
};

export const useGeneratedReports = (params: GeneratedReportsListHookParams = {}) => {
  const { createApolloContext } = useNotification();

  const { data, loading } = useGeneratedReportsMetaQuery({
    variables: {
      inboxItemId: params.inboxItemId || '',
    },
    skip: !params.inboxItemId || params.skip,
  });

  const [downloadGeneratedReportFilesMutation, { loading: downloading }] =
    useDownloadGeneratedReportFilesMutation({
      context: createApolloContext(),
    });

  const [downloadGeneratedReportFilesArchiveMutation, { loading: archiveDownloading }] =
    useDownloadGeneratedReportFilesArchiveMutation({
      context: createApolloContext(),
    });

  const generatedReportsMeta = React.useMemo(
    () => data?.getGeneratedReportsMeta || [],
    [data?.getGeneratedReportsMeta],
  );

  const downloadGeneratedReportFiles = React.useCallback(
    async (
      fileIdList: string | string[],
      action: PiiDataAccessActionType,
      { refetchQueries }: { refetchQueries?: QueryOptions[] } = {},
    ): Promise<FileResponseInfoFragment[]> => {
      if (!params.inboxItemId) return [];
      const { data } = await downloadGeneratedReportFilesMutation({
        variables: {
          inboxItemId: params.inboxItemId,
          fileIdList,
          action,
        },
        refetchQueries,
      });
      if (!data) return [];

      if (action === PiiDataAccessActionType.Downloaded) {
        for (const file of data.downloadGeneratedReportFiles) {
          downloadFileByDownloadUrl(file.downloadUrl, file.filename);
        }
      }

      return data.downloadGeneratedReportFiles;
    },
    [downloadGeneratedReportFilesMutation, params.inboxItemId],
  );

  const downloadGeneratedReportFilesArchive = React.useCallback(
    async (
      fileIdList: string | string[],
      { refetchQueries }: { refetchQueries?: QueryOptions[] } = {},
    ): Promise<GeneratedReportFilesArchiveResponse | null> => {
      if (!params.inboxItemId) return null;
      const { data } = await downloadGeneratedReportFilesArchiveMutation({
        variables: {
          inboxItemId: params.inboxItemId,
          fileIdList,
        },
        refetchQueries,
      });
      if (!data?.downloadGeneratedReportFilesArchive) return null;

      downloadFileByDownloadUrl(
        data.downloadGeneratedReportFilesArchive.downloadUrl,
        data.downloadGeneratedReportFilesArchive.filename,
      );

      return data.downloadGeneratedReportFilesArchive;
    },
    [downloadGeneratedReportFilesArchiveMutation, params.inboxItemId],
  );

  return {
    generatedReportsMeta,
    downloadGeneratedReportFiles,
    downloadGeneratedReportFilesArchive,
    loading,
    downloading,
    archiveDownloading,
  };
};
